import React from 'react';

interface ConceptGraphicsProps {
  type: 'data-mesh' | 'crisis-detection' | 'situation-analysis' | 'field-interface' | 'cash-distribution' |
        'hours-matter' | 'data-driven' | 'field-centered' | 'rapid-deployment' | 'hero-visual' | 'user-centered' | 'open-source';
  className?: string;
  animated?: boolean;
}

const ConceptGraphics: React.FC<ConceptGraphicsProps> = ({
  type,
  className = ''
}) => {

  const spectrumOrange = '#FF6B35';
  const spectrumBlue = '#4A90E2';
  const spectrumDark = '#0D0D0D';
  const spectrumMuted = '#8B8680';
  const spectrumLight = '#ECECE9';

  const renderDataMesh = () => {
    const meshNodes = [
      { x: 200, y: 150, label: 'CLEAR', r: 25, type: 'hub', color: spectrumOrange },
      { x: 100, y: 80, label: 'ACAPS', r: 18, type: 'primary', color: spectrumBlue },
      { x: 300, y: 80, label: 'HDX', r: 18, type: 'primary', color: spectrumBlue },
      { x: 80, y: 200, label: 'OCHA', r: 18, type: 'primary', color: spectrumBlue },
      { x: 320, y: 200, label: 'WFP', r: 18, type: 'primary', color: spectrumBlue },
      { x: 150, y: 50, label: 'INFORM', r: 15, type: 'secondary', color: spectrumMuted },
      { x: 250, y: 250, label: 'FEWS', r: 15, type: 'secondary', color: spectrumMuted },
      { x: 50, y: 130, label: 'UNHCR', r: 15, type: 'secondary', color: spectrumMuted },
      { x: 350, y: 130, label: 'WHO', r: 15, type: 'secondary', color: spectrumMuted },
    ];

    const meshConnections: Array<{x1: number, y1: number, x2: number, y2: number, strength: number}> = [];
    meshNodes.forEach((nodeA, i) => {
      meshNodes.forEach((nodeB, j) => {
        if (i !== j) {
          const distance = Math.sqrt(Math.pow(nodeA.x - nodeB.x, 2) + Math.pow(nodeA.y - nodeB.y, 2));
          if (distance < 120 || nodeA.type === 'hub' || nodeB.type === 'hub') {
            const strength = nodeA.type === 'hub' || nodeB.type === 'hub' ? 0.8 :
                           distance < 80 ? 0.6 :
                           distance < 100 ? 0.4 : 0.2;
            meshConnections.push({
              x1: nodeA.x, y1: nodeA.y,
              x2: nodeB.x, y2: nodeB.y,
              strength
            });
          }
        }
      });
    });

    return (
      <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
        <defs>
          <linearGradient id="meshGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={spectrumOrange} />
            <stop offset="100%" stopColor={spectrumBlue} />
          </linearGradient>
          <pattern id="meshGrid" width="15" height="15" patternUnits="userSpaceOnUse">
            <path d="M 15 0 L 0 0 0 15" fill="none" stroke={spectrumLight} strokeWidth="0.5"/>
          </pattern>
        </defs>

        <rect width="100%" height="100%" fill="url(#meshGrid)" opacity="0.2" />

        {meshConnections.map((connection, i) => (
          <line
            key={i}
            x1={connection.x1}
            y1={connection.y1}
            x2={connection.x2}
            y2={connection.y2}
            stroke={spectrumOrange}
            strokeWidth={connection.strength * 2}
            opacity={connection.strength}
            strokeDasharray={connection.strength > 0.5 ? "none" : "2,2"}
          />
        ))}

        {meshNodes.map((node, i) => (
          <g key={i}>
            <circle
              cx={node.x}
              cy={node.y}
              r={node.r || 10}
              fill={node.type === 'hub' ? "url(#meshGradient)" : spectrumLight}
              stroke={node.color}
              strokeWidth={node.type === 'hub' ? "3" : "2"}
            />
            <text
              x={node.x}
              y={node.y + (node.type === 'hub' ? 0 : 3)}
              textAnchor="middle"
              fontSize={node.type === 'hub' ? "9" : node.r > 12 ? "7" : "6"}
              fill={node.type === 'hub' ? "white" : spectrumDark}
              fontFamily="'JetBrains Mono', Monaco, Consolas, monospace"
              fontWeight={node.type === 'hub' ? "bold" : "normal"}
            >
              {node.label}
            </text>
          </g>
        ))}
      </svg>
    );
  };

  const renderCrisisDetection = () => {
    const crisisPoints = [
      { x: 80, y: 100, severity: 'high', label: 'Flood' },
      { x: 180, y: 140, severity: 'critical', label: 'Conflict' },
      { x: 120, y: 180, severity: 'medium', label: 'Drought' },
      { x: 280, y: 90, severity: 'high', label: 'Storm' },
      { x: 320, y: 160, severity: 'medium', label: 'Fire' },
    ];

    const getSeverityColor = (severity: string) => {
      switch (severity) {
        case 'critical': return '#DC2626';
        case 'high': return spectrumOrange;
        case 'medium': return '#F59E0B';
        default: return spectrumMuted;
      }
    };

    return (
      <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
        <path
          d="M 100 140 Q 110 120 130 110 L 160 100 L 170 120 L 150 170 Q 140 200 120 210 Q 100 200 90 180 Z"
          fill={spectrumLight}
          stroke={spectrumMuted}
          strokeWidth="1.5"
          opacity="0.6"
        />
        <path
          d="M 160 80 L 180 70 L 200 80 L 210 100 L 190 120 L 170 110 Z"
          fill={spectrumLight}
          stroke={spectrumMuted}
          strokeWidth="1.5"
          opacity="0.6"
        />
        <path
          d="M 210 90 L 240 70 Q 270 80 280 100 L 270 130 Q 250 140 230 130 L 220 110 Z"
          fill={spectrumLight}
          stroke={spectrumMuted}
          strokeWidth="1.5"
          opacity="0.6"
        />
        <path
          d="M 300 150 Q 320 145 330 155 L 340 170 Q 330 180 315 175 L 305 165 Z"
          fill={spectrumLight}
          stroke={spectrumMuted}
          strokeWidth="1.5"
          opacity="0.6"
        />

        {crisisPoints.map((point, i) => (
          <g key={i}>
            <path
              d={`M ${point.x} ${point.y - 15}
                  Q ${point.x - 8} ${point.y - 15} ${point.x - 8} ${point.y - 7}
                  Q ${point.x - 8} ${point.y} ${point.x} ${point.y + 5}
                  Q ${point.x + 8} ${point.y} ${point.x + 8} ${point.y - 7}
                  Q ${point.x + 8} ${point.y - 15} ${point.x} ${point.y - 15} Z`}
              fill={getSeverityColor(point.severity)}
              stroke="white"
              strokeWidth="1.5"
            />
            <circle cx={point.x} cy={point.y - 10} r="5" fill="white" opacity="0.9" />
            <line
              x1={point.x}
              y1={point.y - 13}
              x2={point.x}
              y2={point.y - 9}
              stroke={getSeverityColor(point.severity)}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <circle cx={point.x} cy={point.y - 7.5} r="0.5" fill={getSeverityColor(point.severity)} />
            <text
              x={point.x}
              y={point.y + 15}
              textAnchor="middle"
              fontSize="8"
              fontFamily="'JetBrains Mono', Monaco, Consolas, monospace"
              fontWeight="bold"
              fill={getSeverityColor(point.severity)}
            >
              {point.label}
            </text>
          </g>
        ))}

        <circle cx="350" cy="40" r="20" fill={spectrumOrange} stroke="white" strokeWidth="2" />
        <text x="350" y="38" textAnchor="middle" fontSize="10" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" fontWeight="bold" fill="white">AI</text>
        <text x="350" y="48" textAnchor="middle" fontSize="7" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" fill="white">HUB</text>

        {crisisPoints.map((point, i) => (
          <line
            key={`flow-${i}`}
            x1={point.x}
            y1={point.y}
            x2="350"
            y2="40"
            stroke={getSeverityColor(point.severity)}
            strokeWidth="1.5"
            strokeDasharray="4,4"
            opacity="0.4"
          />
        ))}

        <text x="20" y="30" fontSize="9" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" fontWeight="bold" fill={spectrumOrange}>
          REAL-TIME MONITORING
        </text>
      </svg>
    );
  };

  const renderFieldInterface = () => (
    <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
      <rect x="150" y="50" width="100" height="180" fill={spectrumDark} stroke={spectrumOrange} strokeWidth="2"/>
      <rect x="160" y="70" width="80" height="140" fill={spectrumLight}/>

      <rect x="165" y="75" width="70" height="20" fill={spectrumOrange} opacity="0.8"/>
      <text x="200" y="87" textAnchor="middle" fontSize="8" fill="white" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">CLEAR</text>

      <rect x="165" y="100" width="30" height="15" fill={spectrumBlue} opacity="0.6"/>
      <rect x="200" y="100" width="35" height="15" fill={spectrumMuted} opacity="0.4"/>

      <rect x="165" y="120" width="70" height="25" fill={spectrumLight} stroke={spectrumOrange} strokeWidth="1"/>
      <text x="200" y="135" textAnchor="middle" fontSize="7" fill={spectrumDark}>Crisis Alert</text>

      <circle cx="175" cy="155" r={3} fill="#22C55E"/>
      <text x="185" y="158" fontSize="6" fill={spectrumDark}>Active</text>

      <circle cx="175" cy="170" r={3} fill={spectrumBlue}/>
      <text x="185" y="173" fontSize="6" fill={spectrumDark}>Warning</text>

      {[1, 2, 3].map(i => (
        <path
          key={i}
          d={`M ${250 + i * 20} 130 Q ${270 + i * 20} 120 ${290 + i * 20} 130`}
          stroke={spectrumOrange}
          strokeWidth="2"
          fill="none"
          opacity="0.7"
        />
      ))}

      <circle cx="100" cy="150" r={20} fill={spectrumLight} stroke={spectrumOrange} strokeWidth="2"/>
      <text x="100" y="155" textAnchor="middle" fontSize="8" fill={spectrumDark}>👤</text>
      <text x="100" y="180" textAnchor="middle" fontSize="8" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">FIELD TEAM</text>

      <line x1="120" y1="150" x2="150" y2="140" stroke={spectrumOrange} strokeWidth="2" strokeDasharray="5,5" />
    </svg>
  );

  const renderCashDistribution = () => (
    <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
      <rect x="160" y="80" width="80" height="120" fill={spectrumDark}/>
      <rect x="170" y="90" width="60" height="40" fill={spectrumLight}/>
      <text x="200" y="112" textAnchor="middle" fontSize="10" fill={spectrumOrange} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">CASH</text>

      <rect x="175" y="140" width="50" height="3" fill={spectrumOrange}/>
      <rect x="175" y="160" width="50" height="15" fill={spectrumBlue} opacity="0.8"/>

      <g transform="translate(50, 50)">
        <text x="0" y="10" fontSize="8" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">VULNERABILITY MAP</text>
        {Array.from({ length: 16 }).map((_, i) => {
          const x = (i % 4) * 15;
          const y = Math.floor(i / 4) * 15 + 20;
          const intensity = Math.random();
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width="12"
              height="12"
              fill={spectrumOrange}
              opacity={intensity * 0.8}
            />
          );
        })}
      </g>

      <g transform="translate(280, 120)">
        {[1, 2, 3].map(i => (
          <g key={i} transform={`translate(0, ${i * 30})`}>
            <circle cx="20" cy="15" r={12} fill={spectrumLight} stroke={spectrumOrange} strokeWidth="1"/>
            <text x="20" y="18" textAnchor="middle" fontSize="8">👤</text>
            <line x1="8" y1="15" x2="-20" y2="15" stroke={spectrumBlue} strokeWidth="2" strokeDasharray="3,3" />
          </g>
        ))}
      </g>
    </svg>
  );

  const renderSituationAnalysis = () => (
    <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
      <rect x="60" y="40" width="280" height="220" fill={spectrumLight} stroke={spectrumOrange} strokeWidth="2"/>
      <rect x="60" y="40" width="280" height="30" fill={spectrumDark}/>
      <text x="200" y="60" textAnchor="middle" fontSize="12" fill="white" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" fontWeight="bold">
        SITUATION ANALYSIS
      </text>

      <g transform="translate(80, 90)">
        <rect width="120" height="90" fill={spectrumLight} stroke={spectrumMuted} strokeWidth="1"/>
        {[
          { x: 10, y: 10, w: 30, h: 25, intensity: 0.8 },
          { x: 45, y: 15, w: 35, h: 20, intensity: 0.5 },
          { x: 85, y: 12, w: 25, h: 28, intensity: 0.3 },
          { x: 15, y: 45, w: 40, h: 35, intensity: 0.6 },
          { x: 60, y: 50, w: 45, h: 30, intensity: 0.4 }
        ].map((region, i) => (
          <rect
            key={i}
            x={region.x}
            y={region.y}
            width={region.w}
            height={region.h}
            fill={spectrumOrange}
            opacity={region.intensity * 0.7}
          />
        ))}
        <text x="60" y="95" textAnchor="middle" fontSize="7" fill={spectrumDark} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
          CRISIS MAP
        </text>
      </g>

      <g transform="translate(220, 90)">
        <text x="0" y="5" fontSize="8" fill={spectrumDark} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" fontWeight="bold">INDICATORS</text>
        {[
          { label: 'Severity', value: 0.85, color: spectrumOrange },
          { label: 'Urgency', value: 0.7, color: spectrumBlue },
          { label: 'Resources', value: 0.4, color: spectrumMuted },
          { label: 'Access', value: 0.6, color: '#22C55E' }
        ].map((indicator, i) => (
          <g key={i} transform={`translate(0, ${15 + i * 20})`}>
            <text x="0" y="8" fontSize="7" fill={spectrumDark} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
              {indicator.label}
            </text>
            <rect x="0" y="10" width="90" height="6" fill={spectrumLight} stroke={spectrumMuted} strokeWidth="0.5"/>
            <rect x="0" y="10" height="6" width={indicator.value * 90} fill={indicator.color} />
          </g>
        ))}
      </g>

      <g transform="translate(80, 195)">
        <text x="0" y="5" fontSize="8" fill={spectrumDark} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" fontWeight="bold">TIMELINE</text>
        {[...Array(8)].map((_, i) => {
          const height = Math.random() * 30 + 10;
          return (
            <rect
              key={i}
              x={i * 28}
              y={50 - height}
              width="20"
              height={height}
              fill={spectrumBlue}
              opacity={0.7}
            />
          );
        })}
        <line x1="0" y1="50" x2="220" y2="50" stroke={spectrumDark} strokeWidth="1"/>
      </g>

      <g transform="translate(320, 50)">
        <rect x="-15" y="-8" width="30" height="16" fill={spectrumOrange}/>
        <text x="0" y="2" textAnchor="middle" fontSize="7" fill="white" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" fontWeight="bold">
          AI
        </text>
      </g>
    </svg>
  );

  const renderHoursMatter = () => (
    <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
      <circle cx="200" cy="150" r={60} fill={spectrumLight} stroke={spectrumOrange} strokeWidth="4"/>

      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 30 - 90) * Math.PI / 180;
        const x1 = 200 + Math.cos(angle) * 50;
        const y1 = 150 + Math.sin(angle) * 50;
        const x2 = 200 + Math.cos(angle) * 45;
        const y2 = 150 + Math.sin(angle) * 45;
        return (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={spectrumDark} strokeWidth="2"/>
        );
      })}

      <line x1="200" y1="150" x2="200" y2="110" stroke={spectrumBlue} strokeWidth="4" strokeLinecap="round" />
      <line x1="200" y1="150" x2="235" y2="150" stroke={spectrumOrange} strokeWidth="6" strokeLinecap="round" />
      <circle cx="200" cy="150" r={8} fill={spectrumDark}/>

      <text x="200" y="250" textAnchor="middle" fontSize="12" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
        EVERY HOUR COUNTS
      </text>
    </svg>
  );

  const renderDataDriven = () => {
    const dataPoints = [
      { x: 120, y: 180 },
      { x: 140, y: 160 },
      { x: 160, y: 140 },
      { x: 180, y: 120 },
      { x: 200, y: 100 },
      { x: 220, y: 95 },
      { x: 240, y: 90 },
      { x: 260, y: 85 },
      { x: 280, y: 80 }
    ];

    return (
      <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
        <rect x="80" y="60" width="240" height="160" fill={spectrumLight} stroke={spectrumOrange} strokeWidth="2"/>
        <line x1="100" y1="80" x2="100" y2="200" stroke={spectrumDark} strokeWidth="2"/>
        <line x1="100" y1="200" x2="300" y2="200" stroke={spectrumDark} strokeWidth="2"/>

        <polyline
          points={dataPoints.map(p => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke={spectrumOrange}
          strokeWidth="3"
        />

        {dataPoints.map((point, i) => (
          <circle
            key={i}
            cx={point.x}
            cy={point.y}
            r={5}
            fill={spectrumBlue}
          />
        ))}

        <text x="50" y="140" fontSize="10" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" transform="rotate(-90, 50, 140)">
          ACCURACY
        </text>
        <text x="200" y="235" textAnchor="middle" fontSize="10" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
          TIME
        </text>

        <rect x="250" y="100" width="40" height="20" fill={spectrumOrange}/>
        <text x="270" y="112" textAnchor="middle" fontSize="8" fill="white" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">AI</text>
      </svg>
    );
  };

  const renderFieldCentered = () => (
    <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
      <rect x="0" y="200" width="400" height="100" fill={spectrumLight} opacity="0.3"/>

      {[1, 2, 3, 4].map(i => (
        <g key={i} transform={`translate(${50 + i * 80}, 180)`}>
          <polygon points="0,20 20,0 40,20 40,30 0,30" fill={spectrumLight} stroke={spectrumOrange} strokeWidth="1"/>
          <circle cx="20" cy="40" r={8} fill={spectrumBlue} />
        </g>
      ))}

      <rect x="180" y="100" width="40" height="60" fill={spectrumDark}/>
      <rect x="185" y="110" width="30" height="20" fill={spectrumOrange}/>
      <text x="200" y="122" textAnchor="middle" fontSize="8" fill="white" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">HQ</text>

      {[1, 2, 3, 4].map(i => {
        const startX = 200;
        const startY = 140;
        const endX = 70 + i * 80;
        const endY = 200;
        return (
          <line
            key={i}
            x1={startX}
            y1={startY}
            x2={endX}
            y2={endY}
            stroke={spectrumOrange}
            strokeWidth="2"
            strokeDasharray="5,5"
          />
        );
      })}
    </svg>
  );

  const renderOpenSource = () => {
    return (
      <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
        <g transform="translate(180, 80)">
          <rect x="10" y="30" width="40" height="45" fill={spectrumLight} stroke={spectrumOrange} strokeWidth="3"/>
          <path
            d="M 20 30 L 20 10 Q 20 0 30 0 Q 40 0 40 10 L 40 20"
            stroke={spectrumOrange}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
          <circle cx="30" cy="50" r="4" fill={spectrumOrange}/>
          <rect x="28" y="50" width="4" height="8" fill={spectrumOrange}/>
        </g>

        <g transform="translate(50, 140)">
          <text x="0" y="20" fontSize="24" fill={spectrumBlue} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
            {'</>'}
          </text>
          {[0, 1, 2].map((i) => (
            <line
              key={i}
              x1="45"
              y1={5 + i * 10}
              x2={75 + i * 15}
              y2={5 + i * 10}
              stroke={spectrumMuted}
              strokeWidth="2"
            />
          ))}
          <text x="45" y="45" fontSize="6" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
            OPEN SOURCE
          </text>
        </g>

        {[
          { x: 280, y: 140, label: 'ORG A' },
          { x: 320, y: 180, label: 'ORG B' },
          { x: 280, y: 220, label: 'ORG C' }
        ].map((org, i) => (
          <g key={i}>
            <rect
              x={org.x}
              y={org.y}
              width="45"
              height="25"
              fill={spectrumLight}
              stroke={spectrumBlue}
              strokeWidth="2"
            />
            <rect x={org.x + 5} y={org.y + 5} width="35" height="3" fill={spectrumOrange} opacity="0.6" />
            <rect x={org.x + 5} y={org.y + 10} width="25" height="2" fill={spectrumMuted} opacity="0.4"/>
            <rect x={org.x + 5} y={org.y + 14} width="30" height="2" fill={spectrumMuted} opacity="0.4"/>
            <text x={org.x + 22.5} y={org.y + 35} textAnchor="middle" fontSize="6" fill={spectrumDark} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
              {org.label}
            </text>
          </g>
        ))}

        {[0, 1, 2].map((i) => (
          <line
            key={i}
            x1="120"
            x2="280"
            y1="160"
            y2={160 + i * 40}
            stroke={spectrumBlue}
            strokeWidth="2"
            strokeDasharray="4,4"
          />
        ))}

        <text x="200" y="30" textAnchor="middle" fontSize="10" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
          TECHNOLOGY INDEPENDENCE
        </text>
      </svg>
    );
  };

  const renderUserCentered = () => {
    const userTypes = [
      { x: 200, y: 150, label: 'USERS', r: 35, type: 'center' },
      { x: 100, y: 80, label: 'LOCAL', r: 20, type: 'user' },
      { x: 300, y: 80, label: 'NGO', r: 20, type: 'user' },
      { x: 80, y: 220, label: 'FIELD', r: 20, type: 'user' },
      { x: 320, y: 220, label: 'MGMT', r: 20, type: 'user' }
    ];

    return (
      <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
        <circle cx="200" cy="150" r="50" fill={spectrumOrange} opacity="0.1" />
        <circle cx="200" cy="150" r="80" fill="none" stroke={spectrumOrange} strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />

        {userTypes.map((user, i) => (
          <g key={i}>
            {user.type === 'center' ? (
              <>
                <circle cx={user.x} cy={user.y} r={user.r} fill={spectrumOrange} />
                <g>
                  <circle cx="200" cy="140" r="8" fill="white"/>
                  <path d="M 185 165 Q 185 155 193 155 L 207 155 Q 215 155 215 165 L 215 170 L 185 170 Z" fill="white"/>
                </g>
                <text x={user.x} y={user.y + 5} textAnchor="middle" fontSize="10" fill="white" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" fontWeight="bold">
                  {user.label}
                </text>
              </>
            ) : (
              <>
                <line x1="200" y1="150" x2={user.x} y2={user.y} stroke={spectrumBlue} strokeWidth="2" strokeDasharray="3,3" opacity="0.6" />
                <circle cx={user.x} cy={user.y} r={user.r} fill={spectrumLight} stroke={spectrumBlue} strokeWidth="2" />
                <circle cx={user.x} cy={user.y - 5} r="4" fill={spectrumDark}/>
                <path
                  d={`M ${user.x - 8} ${user.y + 8} Q ${user.x - 8} ${user.y + 3} ${user.x - 3} ${user.y + 3} L ${user.x + 3} ${user.y + 3} Q ${user.x + 8} ${user.y + 3} ${user.x + 8} ${user.y + 8} Z`}
                  fill={spectrumDark}
                />
                <text x={user.x} y={user.y + user.r + 12} textAnchor="middle" fontSize="7" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
                  {user.label}
                </text>
              </>
            )}
          </g>
        ))}
      </svg>
    );
  };

  const renderRapidDeployment = () => {
    const timelinePoints = [
      { x: 80, label: 'DETECT', time: 'Hour 1' },
      { x: 140, label: 'ANALYZE', time: 'Hour 2' },
      { x: 200, label: 'DECIDE', time: 'Hour 4' },
      { x: 260, label: 'DEPLOY', time: 'Hour 6' },
      { x: 320, label: 'ASSIST', time: 'Hour 8' }
    ];

    return (
      <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill={spectrumOrange}/>
          </marker>
        </defs>

        <line x1="50" y1="150" x2="350" y2="150" stroke={spectrumOrange} strokeWidth="4" markerEnd="url(#arrowhead)" />

        {timelinePoints.map((point, i) => (
          <g key={i}>
            <circle cx={point.x} cy="150" r={12} fill={spectrumBlue} />
            <text x={point.x} y="155" textAnchor="middle" fontSize="6" fill="white" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
              {i + 1}
            </text>
            <text x={point.x} y="135" textAnchor="middle" fontSize="8" fill={spectrumDark} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
              {point.label}
            </text>
            <text x={point.x} y="175" textAnchor="middle" fontSize="7" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
              {point.time}
            </text>
          </g>
        ))}

        <text x="200" y="35" textAnchor="middle" fontSize="10" fill={spectrumBlue} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
          RAPID DEPLOYMENT
        </text>

        <g transform="translate(50, 220)">
          <text x="0" y="0" fontSize="8" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">TRADITIONAL: 11+ DAYS</text>
          <rect x="0" y="10" width="200" height="8" fill={spectrumMuted} opacity="0.3"/>

          <text x="0" y="35" fontSize="8" fill={spectrumOrange} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">CLEAR: 8 HOURS</text>
          <rect x="0" y="45" width="40" height="8" fill={spectrumOrange} />
        </g>
      </svg>
    );
  };

  const renderHeroVisual = () => (
    <svg className={`w-full h-full ${className}`} viewBox="0 0 400 300" fill="none">
      <circle cx="200" cy="150" r={80} fill="none" stroke={spectrumLight} strokeWidth="1" opacity="0.5"/>
      <circle cx="200" cy="150" r={120} fill="none" stroke={spectrumLight} strokeWidth="1" opacity="0.3"/>

      <defs>
        <radialGradient id="heroGradient" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={spectrumOrange} />
          <stop offset="100%" stopColor={spectrumBlue} />
        </radialGradient>
      </defs>

      <circle cx="200" cy="150" r={40} fill="url(#heroGradient)" />
      <text x="200" y="155" textAnchor="middle" fontSize="12" fill="white" fontFamily="'JetBrains Mono', Monaco, Consolas, monospace" fontWeight="bold">
        CLEAR
      </text>

      {[
        { x: 120, y: 100, region: 'AF' },
        { x: 280, y: 120, region: 'AS' },
        { x: 100, y: 200, region: 'SA' },
        { x: 300, y: 180, region: 'EU' },
        { x: 160, y: 80, region: 'ME' },
        { x: 240, y: 220, region: 'OC' }
      ].map((point, i) => (
        <g key={i}>
          <circle cx={point.x} cy={point.y} r={15} fill={spectrumLight} stroke={spectrumOrange} strokeWidth="2" />
          <text x={point.x} y={point.y + 3} textAnchor="middle" fontSize="8" fill={spectrumDark} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
            {point.region}
          </text>
          <line x1={point.x} y1={point.y} x2="200" y2="150" stroke={spectrumBlue} strokeWidth="2" opacity="0.3" />
        </g>
      ))}

      <circle cx="200" cy="150" r={60} fill="none" stroke={spectrumOrange} strokeWidth="2" strokeDasharray="10,5" />

      <text x="200" y="30" textAnchor="middle" fontSize="10" fill={spectrumBlue} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
        AI-POWERED EARLY WARNING
      </text>

      <text x="200" y="280" textAnchor="middle" fontSize="10" fill={spectrumMuted} fontFamily="'JetBrains Mono', Monaco, Consolas, monospace">
        GLOBAL HUMANITARIAN NETWORK
      </text>
    </svg>
  );

  const renderGraphic = () => {
    switch (type) {
      case 'data-mesh':
        return renderDataMesh();
      case 'crisis-detection':
        return renderCrisisDetection();
      case 'situation-analysis':
        return renderSituationAnalysis();
      case 'field-interface':
        return renderFieldInterface();
      case 'cash-distribution':
        return renderCashDistribution();
      case 'hours-matter':
        return renderHoursMatter();
      case 'data-driven':
        return renderDataDriven();
      case 'field-centered':
        return renderFieldCentered();
      case 'open-source':
        return renderOpenSource();
      case 'rapid-deployment':
        return renderRapidDeployment();
      case 'user-centered':
        return renderUserCentered();
      case 'hero-visual':
        return renderHeroVisual();
      default:
        return renderHeroVisual();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {renderGraphic()}
    </div>
  );
};

export default ConceptGraphics;
